/**
 * Patient Context Indicators Group
 *
 * Self-contained unit that owns the patient context indicator row:
 * data fetching, hover state, popover rendering, edit modals, markdown
 * importer, and analytics. Consumers just supply a `patientId` + `source`
 * — no prop drilling, no duplicated wiring.
 *
 * Used by PatientContextBar (encounter view) and NoteMetadata (note chat
 * view). Adding a new indicator type, edit modal, or analytics event
 * should happen here so both surfaces get it simultaneously.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { useUserState } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks';

import { useIndicatorHover } from '../hooks/useIndicatorHover';
import { usePatientContext } from '../hooks/usePatientContext';

import { PatientAllergiesEditModal } from './PatientAllergiesEditModal';
import { PatientAllergiesPopover } from './PatientAllergiesPopover';
import { PatientDiagnosesEditModal } from './PatientDiagnosesEditModal';
import { PatientDiagnosesPopover } from './PatientDiagnosesPopover';
import { PatientIndicators } from './PatientIndicators';
import { PatientLastEncounterPopover } from './PatientLastEncounterPopover';
import { PatientMedicationsEditModal } from './PatientMedicationsEditModal';
import { PatientMedicationsPopover } from './PatientMedicationsPopover';
import { TREATMENT_PLAN_EDIT_DIALOG_DEFAULTS } from './PatientTreatmentPlanEditModal.constants';
import { PatientTreatmentPlanPopover } from './PatientTreatmentPlanPopover';

export interface PatientContextIndicatorsGroupProps {
  /** Patient ID to fetch context for. When null, nothing renders. */
  patientId: string | null;
  /** Analytics source identifier (e.g. 'encounter', 'note', 'jotaudit'). */
  source: string;
  /** Called when the user clicks "view note" on the last encounter popover. */
  onViewLastEncounterNote?: (noteId: string) => void;
}

/**
 * Renders the full patient context indicator row with all hover popovers,
 * edit modals, and the treatment plan markdown importer. Owns all related
 * state and analytics.
 */
export const PatientContextIndicatorsGroup: React.FC<
  PatientContextIndicatorsGroupProps
> = ({ patientId, source, onViewLastEncounterNote }) => {
  // Fetch patient context data
  const {
    medications,
    diagnoses,
    allergies,
    lastEncounter,
    treatmentPlan,
    loadingState,
    medicationsModuleInfo,
    diagnosesModuleInfo,
    allergiesModuleInfo,
  } = usePatientContext(patientId);

  // Feature flag
  const userState = useUserState();
  const showTreatmentPlan = !!userState?.featureFlags?.treatment_plan;

  // Event tracking
  const { patient: patientTracking, patientContextBar: contextBarTracking } =
    useEventTracking();

  // Global modal system
  const { open } = useModal();

  // Modal state (medications, diagnoses, allergies remain local)
  const [medicationsModalOpen, setMedicationsModalOpen] = useState(false);
  const [diagnosesModalOpen, setDiagnosesModalOpen] = useState(false);
  const [allergiesModalOpen, setAllergiesModalOpen] = useState(false);

  // Hover state per indicator
  const medicationsHover = useIndicatorHover();
  const diagnosesHover = useIndicatorHover();
  const allergiesHover = useIndicatorHover();
  const lastEncounterHover = useIndicatorHover();
  const treatmentPlanHover = useIndicatorHover();

  // Derived popover-open booleans (used for analytics effects)
  const hasPatient = !!patientId;
  const medicationsPopoverOpen =
    medicationsHover.isHovered && !!medicationsHover.anchorEl && hasPatient;
  const diagnosesPopoverOpen =
    diagnosesHover.isHovered && !!diagnosesHover.anchorEl && hasPatient;
  const allergiesPopoverOpen =
    allergiesHover.isHovered && !!allergiesHover.anchorEl && hasPatient;
  const lastEncounterPopoverOpen =
    lastEncounterHover.isHovered && !!lastEncounterHover.anchorEl && hasPatient;
  const treatmentPlanPopoverOpen =
    treatmentPlanHover.isHovered &&
    !!treatmentPlanHover.anchorEl &&
    hasPatient &&
    showTreatmentPlan;

  // Track popover views (fire once per open)
  const prevMedsOpen = useRef(false);
  const prevDiagsOpen = useRef(false);
  const prevAllergiesOpen = useRef(false);
  const prevEncounterOpen = useRef(false);
  const prevTreatmentPlanOpen = useRef(false);

  useEffect(() => {
    if (medicationsPopoverOpen && !prevMedsOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'medications',
        item_count: medications.length,
        source,
      });
    }
    prevMedsOpen.current = medicationsPopoverOpen;
  }, [medicationsPopoverOpen, patientId, medications.length, source, contextBarTracking]);

  useEffect(() => {
    if (diagnosesPopoverOpen && !prevDiagsOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'diagnoses',
        item_count: diagnoses.length,
        source,
      });
    }
    prevDiagsOpen.current = diagnosesPopoverOpen;
  }, [diagnosesPopoverOpen, patientId, diagnoses.length, source, contextBarTracking]);

  useEffect(() => {
    if (allergiesPopoverOpen && !prevAllergiesOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'allergies',
        item_count: allergies.length,
        source,
      });
    }
    prevAllergiesOpen.current = allergiesPopoverOpen;
  }, [allergiesPopoverOpen, patientId, allergies.length, source, contextBarTracking]);

  useEffect(() => {
    if (lastEncounterPopoverOpen && !prevEncounterOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'last_encounter',
        source,
      });
    }
    prevEncounterOpen.current = lastEncounterPopoverOpen;
  }, [lastEncounterPopoverOpen, patientId, source, contextBarTracking]);

  useEffect(() => {
    if (treatmentPlanPopoverOpen && !prevTreatmentPlanOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'treatment_plan',
        source,
      });
    }
    prevTreatmentPlanOpen.current = treatmentPlanPopoverOpen;
  }, [treatmentPlanPopoverOpen, patientId, source, contextBarTracking]);

  // Indicator click handlers (open modal + fire editModalOpened)
  const handleMedicationsClick = useCallback(() => {
    if (!patientId) return;
    setMedicationsModalOpen(true);
    contextBarTracking.editModalOpened({
      patient_id: patientId,
      data_type: 'medications',
      item_count: medications.length,
      source,
    });
  }, [patientId, medications.length, source, contextBarTracking]);

  const handleDiagnosesClick = useCallback(() => {
    if (!patientId) return;
    setDiagnosesModalOpen(true);
    contextBarTracking.editModalOpened({
      patient_id: patientId,
      data_type: 'diagnoses',
      item_count: diagnoses.length,
      source,
    });
  }, [patientId, diagnoses.length, source, contextBarTracking]);

  const handleAllergiesClick = useCallback(() => {
    if (!patientId) return;
    setAllergiesModalOpen(true);
    contextBarTracking.editModalOpened({
      patient_id: patientId,
      data_type: 'allergies',
      item_count: allergies.length,
      source,
    });
  }, [patientId, allergies.length, source, contextBarTracking]);

  const handleTreatmentPlanEdit = useCallback(() => {
    if (!patientId) return;
    open(MODAL_TYPES.TREATMENT_PLAN_EDIT, {
      patientId,
      source,
      ...TREATMENT_PLAN_EDIT_DIALOG_DEFAULTS,
    });
    contextBarTracking.editModalOpened({
      patient_id: patientId,
      data_type: 'treatment_plan',
      item_count: treatmentPlan ? 1 : 0,
      source,
    });
  }, [patientId, treatmentPlan, source, contextBarTracking, open]);

  // "Updated" callbacks — fire coarse patient.updated tracking so dashboards
  // see which data types got touched via any context-bar surface.
  const handleMedicationsUpdated = useCallback(() => {
    if (!patientId) return;
    patientTracking.updated(patientId, {
      source: 'context_bar',
      fields_updated: ['medications'],
    });
  }, [patientId, patientTracking]);

  const handleDiagnosesUpdated = useCallback(() => {
    if (!patientId) return;
    patientTracking.updated(patientId, {
      source: 'context_bar',
      fields_updated: ['diagnoses'],
    });
  }, [patientId, patientTracking]);

  const handleAllergiesUpdated = useCallback(() => {
    if (!patientId) return;
    patientTracking.updated(patientId, {
      source: 'context_bar',
      fields_updated: ['allergies'],
    });
  }, [patientId, patientTracking]);

  // Edit-from-popover handlers (fire editFromPopoverClicked, then open modal)
  const handleMedicationsEditFromPopover = useCallback(() => {
    if (!patientId) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patientId,
      data_type: 'medications',
      source,
    });
    handleMedicationsClick();
  }, [patientId, source, contextBarTracking, handleMedicationsClick]);

  const handleDiagnosesEditFromPopover = useCallback(() => {
    if (!patientId) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patientId,
      data_type: 'diagnoses',
      source,
    });
    handleDiagnosesClick();
  }, [patientId, source, contextBarTracking, handleDiagnosesClick]);

  const handleAllergiesEditFromPopover = useCallback(() => {
    if (!patientId) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patientId,
      data_type: 'allergies',
      source,
    });
    handleAllergiesClick();
  }, [patientId, source, contextBarTracking, handleAllergiesClick]);

  const handleTreatmentPlanEditFromPopover = useCallback(() => {
    if (!patientId) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patientId,
      data_type: 'treatment_plan',
      source,
    });
    handleTreatmentPlanEdit();
  }, [patientId, source, contextBarTracking, handleTreatmentPlanEdit]);

  // "View note" analytics wrapper
  const handleViewNote = useCallback(
    (noteId: string) => {
      if (patientId) {
        contextBarTracking.viewNoteClicked({
          patient_id: patientId,
          note_id: noteId,
          source,
        });
      }
      onViewLastEncounterNote?.(noteId);
    },
    [patientId, source, contextBarTracking, onViewLastEncounterNote],
  );

  if (!patientId) {
    return null;
  }

  return (
    <>
      <PatientIndicators
        medicationsHover={medicationsHover}
        diagnosesHover={diagnosesHover}
        allergiesHover={allergiesHover}
        lastEncounterHover={lastEncounterHover}
        treatmentPlanHover={showTreatmentPlan ? treatmentPlanHover : undefined}
        onMedicationsClick={handleMedicationsClick}
        onDiagnosesClick={handleDiagnosesClick}
        onAllergiesClick={handleAllergiesClick}
      />

      {/* Medications Popover */}
      <PatientMedicationsPopover
        medications={medications}
        anchorEl={medicationsHover.anchorEl}
        open={medicationsPopoverOpen}
        onClose={() => {
          medicationsHover.handlePopoverLeave();
        }}
        onMouseEnter={medicationsHover.handlePopoverEnter}
        onMouseLeave={medicationsHover.handlePopoverLeave}
        isLoading={loadingState.medications}
        onEdit={handleMedicationsEditFromPopover}
      />

      {/* Diagnoses Popover */}
      <PatientDiagnosesPopover
        diagnoses={diagnoses}
        anchorEl={diagnosesHover.anchorEl}
        open={diagnosesPopoverOpen}
        onClose={() => {
          diagnosesHover.handlePopoverLeave();
        }}
        onMouseEnter={diagnosesHover.handlePopoverEnter}
        onMouseLeave={diagnosesHover.handlePopoverLeave}
        isLoading={loadingState.diagnoses}
        onEdit={handleDiagnosesEditFromPopover}
      />

      {/* Allergies Popover */}
      <PatientAllergiesPopover
        allergies={allergies}
        anchorEl={allergiesHover.anchorEl}
        open={allergiesPopoverOpen}
        onClose={() => {
          allergiesHover.handlePopoverLeave();
        }}
        onMouseEnter={allergiesHover.handlePopoverEnter}
        onMouseLeave={allergiesHover.handlePopoverLeave}
        isLoading={loadingState.allergies}
        onEdit={handleAllergiesEditFromPopover}
      />

      {/* Last Encounter Popover */}
      <PatientLastEncounterPopover
        data={lastEncounter}
        anchorEl={lastEncounterHover.anchorEl}
        open={lastEncounterPopoverOpen}
        onClose={() => {
          lastEncounterHover.handlePopoverLeave();
        }}
        onMouseEnter={lastEncounterHover.handlePopoverEnter}
        onMouseLeave={lastEncounterHover.handlePopoverLeave}
        isLoading={loadingState.lastEncounter}
        onViewNote={handleViewNote}
      />

      {/* Medications Edit Modal */}
      {medicationsModuleInfo && (
        <PatientMedicationsEditModal
          open={medicationsModalOpen}
          onClose={() => setMedicationsModalOpen(false)}
          patientId={patientId}
          medications={medications}
          moduleInfo={medicationsModuleInfo}
          onMedicationsUpdated={handleMedicationsUpdated}
          source={source}
        />
      )}

      {/* Diagnoses Edit Modal */}
      {diagnosesModuleInfo && (
        <PatientDiagnosesEditModal
          open={diagnosesModalOpen}
          onClose={() => setDiagnosesModalOpen(false)}
          patientId={patientId}
          diagnoses={diagnoses}
          moduleInfo={diagnosesModuleInfo}
          onDiagnosesUpdated={handleDiagnosesUpdated}
          source={source}
        />
      )}

      {/* Allergies Edit Modal */}
      {allergiesModuleInfo && (
        <PatientAllergiesEditModal
          open={allergiesModalOpen}
          onClose={() => setAllergiesModalOpen(false)}
          patientId={patientId}
          allergies={allergies}
          moduleInfo={allergiesModuleInfo}
          onAllergiesUpdated={handleAllergiesUpdated}
          source={source}
        />
      )}

      {/* Treatment Plan Popover */}
      {showTreatmentPlan && (
        <PatientTreatmentPlanPopover
          data={treatmentPlan}
          anchorEl={treatmentPlanHover.anchorEl}
          open={treatmentPlanPopoverOpen}
          onClose={() => {
            treatmentPlanHover.handlePopoverLeave();
          }}
          onMouseEnter={treatmentPlanHover.handlePopoverEnter}
          onMouseLeave={treatmentPlanHover.handlePopoverLeave}
          isLoading={loadingState.treatmentPlan}
          onEdit={handleTreatmentPlanEditFromPopover}
        />
      )}

    </>
  );
};
